/**
 * screens/admin/adminBypassLedger.tsx — N-3 · RLS bypass ledger.
 *
 * INTERNAL CONSOLE ONLY. SKPLATFORM-UICC-006 §8 / DB-006 S5.
 *
 * ── THE QUESTION THIS SCREEN ANSWERS ──────────────────────────────────────
 *
 * "Which code paths are taking the bypass, and how often" — NOT "what happened
 * at 14:32". So it is GROUPED BY CALL SITE, and the count-per-call-site
 * summary sits ABOVE the log, because that summary is more valuable than the
 * log itself. A rising count on a single call site is the signal worth
 * surfacing, so each row carries a 7-day delta.
 *
 * ── WHAT IS DELIBERATELY ABSENT ───────────────────────────────────────────
 *
 * NO ROW ACTIONS AT ALL. This ledger is append-only in the database — UPDATE
 * and DELETE raise, whatever the role — and the UI offers nothing that
 * suggests otherwise: no edit, no delete, no annotate, no dismiss. A control
 * that implies a mutable audit trail is worse than a missing feature.
 *
 * Reasons are shown IN FULL, never truncated with an ellipsis. The whole point
 * of the required-reason constraint is that somebody reads it.
 *
 * ── WHY IT MAY BE EMPTY, AND WHY THAT IS NOT A BUG ────────────────────────
 *
 * The runtime role holds INSERT and no SELECT on this table, by design: an
 * audit trail the audited party can read and reason about is a weaker audit
 * trail. Reads therefore need a separate REPORTING role, which does not exist
 * on the API yet. Until it does this screen renders its empty state — which is
 * why that state is written to explain itself rather than to look broken.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, ScrollView } from 'react-native';

import { useTheme } from '../../context/themeContext';
import { SettingsHeader, Panel, DataTable, Tag, type Column } from '../../components/platform/primitives';
import { PEmpty } from '../../components/platform/controls';
import { FS, FW } from '../../utils/platformTokens';
import { listRlsBypass, listRlsBypassSites } from '../../services/platform/platformApi';
import type { RlsBypassRow, RlsBypassSiteCount } from '../../schema/platform/types';

const AdminBypassLedger: React.FC = () => {
  const { theme } = useTheme();
  const c = theme.colors;
  const [sites, setSites] = useState<RlsBypassSiteCount[]>([]);
  const [rows, setRows] = useState<RlsBypassRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void (async () => {
      try {
        const [s, r] = await Promise.all([
          listRlsBypassSites().catch(() => [] as RlsBypassSiteCount[]),
          listRlsBypass().catch(() => [] as RlsBypassRow[]),
        ]);
        setSites(s);
        setRows(r);
      } finally { setLoading(false); }
    })();
  }, []);

  /**
   * Fall back to deriving the rollup from the log when the dedicated endpoint
   * is unavailable. The grouping is the point of the screen, so it should not
   * disappear just because one of two reads failed.
   */
  const grouped = useMemo<RlsBypassSiteCount[]>(() => {
    if (sites.length > 0) return [...sites].sort((a, b) => b.last_7d - a.last_7d);
    const map = new Map<string, number>();
    for (const r of rows) map.set(r.call_site, (map.get(r.call_site) ?? 0) + 1);
    return [...map.entries()]
      .map(([call_site, total]) => ({ call_site, total, last_7d: total, previous_7d: 0 }))
      .sort((a, b) => b.total - a.total);
  }, [sites, rows]);

  const siteColumns: Column<RlsBypassSiteCount>[] = [
    {
      key: 'call_site',
      header: 'Call site',
      render: (s) => (
        <Text selectable style={{ fontSize: FS.sm, color: c.ink, fontFamily: theme.font.mono }}>
          {s.call_site}
        </Text>
      ),
    },
    {
      key: 'last_7d',
      header: 'Last 7 days',
      width: 120,
      align: 'right',
      render: (s) => (
        <Text style={{ fontSize: FS.sm, color: c.ink, fontFamily: theme.font.mono }}>
          {s.last_7d.toLocaleString()}
        </Text>
      ),
    },
    {
      /**
       * The delta, not just the count. A call site that doubled week over week
       * is the finding; a call site with a large but flat count is just how the
       * system works.
       */
      key: 'delta',
      header: 'Change',
      width: 120,
      align: 'right',
      render: (s) => {
        if (s.previous_7d === 0) return <Text style={{ fontSize: FS.xs, color: c.ink4 }}>—</Text>;
        const pct = Math.round(((s.last_7d - s.previous_7d) / s.previous_7d) * 100);
        const rising = pct > 25;
        return (
          <Text
            style={{
              fontSize: FS.sm,
              color: rising ? c.warn : c.ink3,
              fontFamily: theme.font.mono,
              fontWeight: rising ? FW.medium : FW.regular,
            }}
          >
            {pct > 0 ? '+' : ''}{pct}%
          </Text>
        );
      },
    },
    {
      key: 'total',
      header: 'Total',
      width: 110,
      align: 'right',
      render: (s) => (
        <Text style={{ fontSize: FS.sm, color: c.ink3, fontFamily: theme.font.mono }}>
          {s.total.toLocaleString()}
        </Text>
      ),
    },
  ];

  const logColumns: Column<RlsBypassRow>[] = [
    {
      key: 'when',
      header: 'Time',
      width: 160,
      render: (r) => (
        <Text style={{ fontSize: FS.xs, color: c.ink3, fontFamily: theme.font.mono }}>
          {new Date(r.occurred_at).toISOString().slice(0, 19).replace('T', ' ')}
        </Text>
      ),
    },
    {
      key: 'call_site',
      header: 'Call site',
      width: 260,
      render: (r) => (
        <Text selectable style={{ fontSize: FS.xs, color: c.ink2, fontFamily: theme.font.mono }}>
          {r.call_site}
        </Text>
      ),
    },
    {
      /* Never truncated. Somebody has to read this. */
      key: 'reason',
      header: 'Reason',
      render: (r) => (
        <Text style={{ fontSize: FS.sm, color: c.ink, lineHeight: 19 }}>{r.reason}</Text>
      ),
    },
    {
      key: 'actor',
      header: 'Actor',
      width: 150,
      render: (r) => (
        <Text style={{ fontSize: FS.xs, color: c.ink3, fontFamily: theme.font.mono }}>
          {r.actor_user_id ?? '—'}
          {r.actor_realm ? ` · ${r.actor_realm}` : ''}
        </Text>
      ),
    },
    {
      key: 'tenant',
      header: 'Tenant context',
      width: 200,
      render: (r) => (
        <Text style={{ fontSize: FS.xs, color: c.ink3, fontFamily: theme.font.mono }}>
          {r.context_org_id ?? 'cross-tenant'}
        </Text>
      ),
    },
    {
      key: 'db_user',
      header: 'DB user',
      width: 120,
      render: (r) => (
        <Text style={{ fontSize: FS.xs, color: c.ink3, fontFamily: theme.font.mono }}>{r.db_user}</Text>
      ),
    },
  ];

  return (
    <ScrollView contentContainerStyle={{ padding: 28, maxWidth: 1400, width: '100%', alignSelf: 'center' }}>
      <SettingsHeader
        title="RLS bypass ledger"
        sub="Every activation of the row-level-security escape hatch, with the reason given. Append-only — Softknack internal."
      />

      {/* The rollup FIRST. This is the screen's actual subject. */}
      <Panel
        title="By call site"
        sub="Which code paths take the bypass, and how often. A rising count on one call site is the signal worth chasing."
      >
        <DataTable
          columns={siteColumns}
          rows={grouped}
          keyExtractor={(s) => s.call_site}
          minWidth={720}
          empty={
            <PEmpty
              title={loading ? 'Loading the ledger…' : 'No bypass activations recorded'}
              children={
                <Text style={{ fontSize: FS.sm, color: c.ink3, lineHeight: 19 }}>
                  The runtime role may write to this ledger and may not read it — an audit trail
                  the audited party can read is a weaker audit trail. Reads require a separate
                  reporting role, so an empty table here may mean the reporting connection is not
                  yet configured rather than that nothing has happened.
                </Text>
              }
            />
          }
        />
      </Panel>

      <View style={{ height: 20 }} />

      <Panel title="Log" sub="Newest first. Reasons are shown in full.">
        <DataTable
          columns={logColumns}
          rows={rows}
          keyExtractor={(r) => r.id}
          minWidth={1200}
          empty={<PEmpty title="Nothing to show for this period" />}
        />
      </Panel>
    </ScrollView>
  );
};

export default AdminBypassLedger;
